import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { usePermissions } from '../../PermissionsContext';
import SeedSubstitutesField from './SeedSubstitutesField';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import './SeedPopup.css';

const SeedPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    // Себестоимость видят/правят только с правом finance (Epic 8 фаза 2)
    const { can } = usePermissions();
    const [form, setForm] = useState({
        seedsCategoryID: '', seedsName: '', varietyName: '', varietyLatinName: '',
        seedsperlb: '', combinedSeedsName: '', cost_per_pound: '',
    });
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/seedscategory')
            .then(r => setCategories(r.data.items || [])).catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                seedsCategoryID:  editData.seedsCategoryID  || '',
                seedsName:        editData.seedsName        || '',
                varietyName:      editData.varietyName      || '',
                varietyLatinName: editData.varietyLatinName || '',
                seedsperlb:       editData.seedsperlb       || '',
                combinedSeedsName:editData.combinedSeedsName|| '',
                cost_per_pound:   editData.cost_per_pound   ?? '',
            });
        } else {
            setForm({ seedsCategoryID:'', seedsName:'', varietyName:'', varietyLatinName:'',
                      seedsperlb:'', combinedSeedsName:'', cost_per_pound:'' });
        }
        setError('');
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const setField = (f) => (e) => {
        const val = e.target.value;
        setForm(p => {
            const updated = { ...p, [f]: val };
            // Auto-build combinedSeedsName from seedsName + varietyName
            if (f === 'seedsName' || f === 'varietyName') {
                const name    = f === 'seedsName'    ? val : p.seedsName;
                const variety = f === 'varietyName'  ? val : p.varietyName;
                updated.combinedSeedsName = [name, variety].filter(Boolean).join(' ');
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.seedsName.trim()) { setError('Seed name is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/seed-popup/${editData.seedsID}`, form);
            } else {
                await api.post('/api/seed-popup', form);
            }
            onSaved(); onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup seed-popup">
                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData
                                ? `Edit Seed — ${editData.seedsName || `#${editData.seedsID}`}`
                                : 'Add Seed'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? `ID #${editData.seedsID}` : 'Fill in seed details'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
                    </button>
                </div>

                <div className="wm-popup__body">
                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">Seed Name *</span>
                            <input className="kit-field__input" value={form.seedsName}
                                onChange={setField('seedsName')} placeholder="e.g. Carrot" autoFocus={!editData} />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Category</span>
                            <select className="kit-field__input" value={form.seedsCategoryID}
                                onChange={e => setForm(p => ({ ...p, seedsCategoryID: e.target.value }))}>
                                <option value="">— Select category —</option>
                                {categories.map(c => (
                                    <option key={c.categoryID} value={c.categoryID}>{c.categoryName}</option>
                                ))}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Variety Name</span>
                            <input className="kit-field__input" value={form.varietyName}
                                onChange={setField('varietyName')} placeholder="e.g. Imperator 58" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Latin Name</span>
                            <input className="kit-field__input" value={form.varietyLatinName}
                                onChange={setField('varietyLatinName')} placeholder="e.g. Daucus carota"
                                style={{ fontStyle: form.varietyLatinName ? 'italic' : 'normal' }} />
                        </div>

                        <div className="kit-field seed-seedsperlb-field">
                            <span className="kit-field__label">Seeds per lb</span>
                            <input className="kit-field__input" type="number" min="0"
                                value={form.seedsperlb} onChange={setField('seedsperlb')} placeholder="e.g. 23000" />
                        </div>

                        {can('finance') && (
                            <div className="kit-field">
                                <span className="kit-field__label">Cost / lb ($)</span>
                                <input className="kit-field__input" type="number" min="0" step="0.0001"
                                    value={form.cost_per_pound} onChange={setField('cost_per_pound')} placeholder="e.g. 12.50" />
                            </div>
                        )}

                        <div className="kit-field seed-combined-field">
                            <span className="kit-field__label">
                                Combined Name
                                <span className="seed-combined-hint"> — shown in dropdowns</span>
                            </span>
                            <input className="kit-field__input" value={form.combinedSeedsName}
                                onChange={setField('combinedSeedsName')}
                                placeholder="e.g. Carrot Imperator 58" />
                        </div>

                        {editData && <SeedSubstitutesField seedsID={editData.seedsID} />}

                    </div>
                </div>

                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Seed')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SeedPopup;
